import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { RadioButton } from 'primereact/radiobutton';
import classes from './selectContractType.module.scss';
import { useStore } from 'hooks/useStore';

const SelectContractType = observer(() => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { BidAnalysisStore } = useStore();

  // Read contract type from URL params on mount
  useEffect(() => {
    const typeFromUrl = searchParams.get('type') as 'mas' | 'nc' | null;
    if (typeFromUrl && !BidAnalysisStore.selectedContractType) {
      BidAnalysisStore.setContractType(typeFromUrl);
    }
  }, [searchParams, BidAnalysisStore]);

  // Static contract types - no need for mock data
  const contractTypes = [
    {
      id: 'nc' as const,
      name: 'NC (National Contract)',
      description: 'Direct government contracting through FSS or BPA',
    },
    {
      id: 'mas' as const,
      name: 'MAS (Multiple Award Schedule)',
      description: 'GSA schedule contracts for multiple agencies',
    },
  ];

  const handleContractTypeSelect = (type: 'mas' | 'nc') => {
    BidAnalysisStore.setContractType(type);
    // Update URL params
    setSearchParams({ type });
  };

  const handleNext = async () => {
    if (BidAnalysisStore.selectedContractType) {
      try {
        // Fetch opportunities from API when moving to next step
        await BidAnalysisStore.fetchOpportunityIterations(BidAnalysisStore.selectedContractType);
        navigate(`/bdportal/bid-analysis/wizard/opportunity?type=${BidAnalysisStore.selectedContractType}`);
      } catch (error) {
        console.error('Error fetching opportunities:', error);
        // Still navigate, error will be shown on the next page
        navigate(`/bdportal/bid-analysis/wizard/opportunity?type=${BidAnalysisStore.selectedContractType}`);
      }
    }
  };

  const handleBack = () => {
    navigate('/bdportal/bid-analysis');
  };

  return (
    <div className={classes.wizardContainer}>
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.headerContent}>
          <div className={classes.logo}>
            <span>gsms</span>
          </div>
          <h1>Create New Bid Analysis</h1>
        </div>
      </div>

      <div className={classes.contentContainer}>
        {/* Progress Stepper */}
        <div className={classes.stepperContainer}>
          <div className={classes.stepper}>
            <div className={classes.stepLine}></div>
            <div className={classes.stepLineProgress} style={{ width: '0%' }}></div>

            <div className={`${classes.step} ${classes.stepActive}`}>
              <div className={`${classes.stepCircle} ${classes.stepCircleActive}`}>1</div>
              <div className={classes.stepLabel}>Contract Type</div>
            </div>

            <div className={classes.step}>
              <div className={classes.stepCircle}>2</div>
              <div className={classes.stepLabel}>Opportunity List</div>
            </div>

            <div className={classes.step}>
              <div className={classes.stepCircle}>3</div>
              <div className={classes.stepLabel}>Product Type</div>
            </div>

            <div className={classes.step}>
              <div className={classes.stepCircle}>4</div>
              <div className={classes.stepLabel}>Confirmation</div>
            </div>
          </div>
        </div>

        {/* Page Title */}
        <h2 className={classes.pageTitle}>Select Contract Type</h2>
        <p className={classes.pageSubtitle}>
          Choose the contract type that best matches your government bid opportunity.
        </p>

        {/* Contract Type Selection */}
        <div className={classes.selectionSection}>
          <h3>Select Primary Contract Type</h3>
          <div className={classes.contractTypeGrid}>
            {contractTypes.map((contractType) => (
              <Card
                key={contractType.id}
                className={`${classes.contractTypeCard} ${
                  BidAnalysisStore.selectedContractType === contractType.id ? classes.contractTypeCardSelected : ''
                }`}
                onClick={() => handleContractTypeSelect(contractType.id)}
              >
                <div className={classes.cardHeader}>
                  <RadioButton
                    inputId={contractType.id}
                    value={contractType.id}
                    checked={BidAnalysisStore.selectedContractType === contractType.id}
                    onChange={() => handleContractTypeSelect(contractType.id)}
                  />
                  <h4>{contractType.name}</h4>
                </div>
                <p className={classes.cardDescription}>{contractType.description}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className={classes.actionButtons}>
          <Button label="Back" icon="pi pi-arrow-left" onClick={handleBack} outlined />
          <Button
            label="Next"
            icon="pi pi-arrow-right"
            iconPos="right"
            onClick={handleNext}
            disabled={!BidAnalysisStore.selectedContractType}
            loading={BidAnalysisStore.isLoadingOpportunities}
          />
        </div>
      </div>
    </div>
  );
});

export default SelectContractType;
